import axios from 'axios';
import { call, all, put, takeLatest, select, delay } from 'redux-saga/effects';

import * as promotionKitReportActions from './promotion-kit-report-actions';

import { saveAs } from 'file-saver';

import {
  GET_LIST_ALL_PROMOTION_KIT_REPORTS_REQUEST,
  GET_LIST_SELECTS_REQUEST,
  SHARE_CSV
} from './promotion-kit-report-constants';

import notification from '~/services/notification';
import promotionKitReportServices from './promotion-kit-report-service';

import { ISA_ERROR } from '~/constants';

import { AppStateType } from '../root-reducer';

import { formatDateTime, formatMessage } from '~/i18n/translate';

let cancel: any;
const CancelToken = axios.CancelToken;

export function* getAllPromotionKitReport({
  type,
  payload
}: {
  type: any;
  payload: {
    date: Date[];
    queryString: string;
    client: {
      externalCode: string;
    };
  };
}) {
  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(promotionKitReportActions.setLoadingData(true));

    const { rowsPerPage, page, order, orderBy } = yield select((state: AppStateType) => state.promotionKitReport.list);

    const params = {
      startDate: payload?.date
        ? payload?.date[0]?.toISOString().split('T')[0]
        : new Date(new Date().toISOString().split('T')[0]),
      endDate: payload?.date
        ? payload?.date[1]?.toISOString().split('T')[0]
        : new Date(new Date().toISOString().split('T')[0]),
      queryString: payload?.queryString ?? '',
      clientExternalCode: payload?.client?.externalCode ?? '',
      skip: page === 0 ? 0 : page * rowsPerPage,
      limit: rowsPerPage,
      order,
      orderBy
    };

    const [{ data: promotionsKitsReports }, { data: rowsCount }] = yield all([
      call(
        promotionKitReportServices.getListAllPromotionKitReports,
        {
          ...params
        },
        {
          cancelToken
        }
      ),
      call(promotionKitReportServices.getRowCount, {
        ...params
      })
    ]);

    yield call(getSelects, payload?.client ?? '');

    yield put(
      promotionKitReportActions.getAllPromotionKitReportsSuccess({
        promotionsKitsReports,
        rowsCount: rowsCount[0]?.totalCount
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(promotionKitReportActions.setLoadingData(false));
  }
}

export function* getSelects({ queryText, skip }: any) {
  try {
    yield delay(200);
    const { externalCode } = yield select(state => state.auth.user);
    const { selects } = yield select((state: AppStateType) => state.promotionKitReport.list);

    yield put(promotionKitReportActions.setLoadingSelects(true));

    const { data: clients } = yield call(promotionKitReportServices.getClients, {
      userExternalCode: externalCode,
      queryString: queryText ?? '',
      skip
    });

    const existingClientsIds = selects?.clients?.map(client => client?.externalCode);
    const filteredClients = clients?.filter(client => !existingClientsIds?.includes(client?.externalCode));
    const parsedClients = [...selects?.clients, ...filteredClients] ?? [];

    yield put(promotionKitReportActions.getListSelectsSuccess({ selects: { clients: parsedClients } }));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(promotionKitReportActions.setLoadingSelects(false));
  }
}

export function* shareCSV({ payload }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    const params = {
      startDate: payload?.date
        ? payload?.date[0]?.toISOString().split('T')[0]
        : new Date(new Date().toISOString().split('T')[0]),
      endDate: payload?.date
        ? payload?.date[1]?.toISOString().split('T')[0]
        : new Date(new Date().toISOString().split('T')[0]),
      queryString: payload?.queryString ?? undefined,
      clientExternalCode: payload?.client?.externalCode ?? undefined,
      order: 'desc',
      orderBy: 'date'
    };

    const { data: csv } = yield call(promotionKitReportServices.getXslx, params);

    const blob = new Blob([csv], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    saveAs(blob, `Promoções Kit/Combos - ${formatDateTime(new Date())}.xlsx`);
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export default all([
  takeLatest(GET_LIST_ALL_PROMOTION_KIT_REPORTS_REQUEST, getAllPromotionKitReport),
  takeLatest(GET_LIST_SELECTS_REQUEST, getSelects),
  takeLatest(SHARE_CSV, shareCSV)
]);
